import { PauseIcon } from '@hugeicons/core-free-icons';
import { Slot } from 'radix-ui';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { Icon, type IconData } from '../primitives/icon.js';

export interface ModuleRailProps {
  /** Landmark label. */
  label?: string;
  /** ModuleRailItem elements. A second group (e.g. platform administration) can be passed in `footer`. */
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}

/**
 * Thin column of product modules at the far left of the AppShell. Each item shows an icon and a short visible
 * label (never icon-only); the selected module has a background, an inset bar and bold text (not color only).
 */
export function ModuleRail({ label = 'Módulos', children, footer, className }: ModuleRailProps) {
  return (
    <nav aria-label={label} className={cn('flex h-full w-full flex-col items-stretch py-3', className)}>
      <ul className="grid gap-1 px-1.5">{children}</ul>
      {footer ? <ul className="mt-auto grid gap-1 border-t border-border-subtle px-1.5 pt-3">{footer}</ul> : null}
    </nav>
  );
}

export interface ModuleRailItemProps {
  /** Short visible label under the icon (one or two words). */
  label: string;
  /** Full name for assistive tech and the hover title (defaults to `label`). */
  title?: string;
  icon: IconData;
  selected?: boolean;
  /** Paused / not enabled: shows a pause mark and appends the text to the accessible name. */
  status?: string;
  href?: string;
  /** Render the single child (e.g. a router Link) with rail styles. */
  asChild?: boolean;
  children?: React.ReactElement;
}

export function ModuleRailItem({ label, title, icon, selected, status, href, asChild, children }: ModuleRailItemProps) {
  const name = `${title ?? label}${status ? ` (${status})` : ''}`;
  const className = cn(
    'relative flex min-h-14 w-full flex-col items-center justify-center gap-1 rounded-control px-1 py-2 text-center transition-colors duration-[var(--ef-motion-fast)]',
    'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus',
    selected
      ? 'bg-accent-selected font-semibold text-accent-on-selected before:absolute before:inset-y-2 before:-left-1.5 before:w-1 before:rounded-full before:bg-current'
      : 'font-medium text-text-secondary hover:bg-surface-default hover:text-text-primary',
  );
  const content = (
    <>
      <span className="relative">
        <Icon icon={icon} className="size-5" />
        {status ? (
          <span className="absolute -bottom-1 -right-2 inline-flex size-3.5 items-center justify-center rounded-full border border-border-subtle bg-surface-default text-text-secondary">
            <Icon icon={PauseIcon} className="size-2.5" strokeWidth={2.5} />
          </span>
        ) : null}
      </span>
      <span className="text-[11px] leading-tight [overflow-wrap:anywhere]" aria-hidden>
        {label}
      </span>
      <span className="sr-only">{name}</span>
    </>
  );
  const common = { className, title: name, 'aria-current': selected ? ('page' as const) : undefined, 'data-ef-rail-item': '' };
  return (
    <li>
      {asChild && children ? (
        <Slot.Root {...common}>{React.cloneElement(children, {}, content)}</Slot.Root>
      ) : (
        <a href={href} {...common}>
          {content}
        </a>
      )}
    </li>
  );
}
